import * as React from 'react';
import { useEffect, useState, useMemo } from 'react';
import { useKeycloak } from '@react-keycloak/web';
import { format } from 'date-fns';
import { useIntl } from 'react-intl';
import {
  Button,
  Collapse,
  Theme,
  IconButton,
  Typography,
  Table,
  Card,
  CardContent,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Radio,
  Box,
  Alert,
  AlertTitle,
} from '@mui/material/';
import makeStyles from '@mui/styles/makeStyles';
import withStyles from '@mui/styles/withStyles';
import createStyles from '@mui/styles/createStyles';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import CloseIcon from '@mui/icons-material/Close';
import RadioGroup from '@mui/material/RadioGroup';
import FormControlLabel from '@mui/material/FormControlLabel';
import FormControl from '@mui/material/FormControl';

import BookingDetailHeader from './BookingDetailHeader';
import PassengerDetailCard from './Passenger/PassengerDetailCard';
import PricingDetailsCard from './PricingDetailsCard';
import CustomDialog from './Dialogs';
import FlightDetailCard from './FlightDetailCard';
import { SelectedAmendBookings } from './RebookFlight/RebookFlightCard/NskRebook';
import NskRebookMain from './RebookFlight/RebookFlightCard/NskRebookMain';
import { RebookProvider } from './RebookFlight/RebookContext';
import PassengersEditForm from './Passenger/PassengersEditForm';
import ContactsDetailCard from './Contacts/ContactsDetailCard';

import { ISourceConfig } from '../models';
import { useTypedSelector } from '../reducers';
import { isNdcFlightOnlyBooking, isTTSBooking, isNdcBooking, isNskBooking } from '../util/booking-type-utils';

import CancelBooking from './CancelBooking/CancelBooking';
import { useRetrieveAirports } from '../hooks/use-retrieve-airports';
import FlightChanges from './FlightChanges/FlightChanges';

import { NdcBookingSource } from '@tff/types/ndc';
import ChargesSummary from './ChargesSummary';
import { useMessages } from '../hooks/use-messages';
import ReactJson from 'react-json-view';
import { CheckDifferences } from '../util/check-differences';
import { useConfirmBooking } from '../hooks/use-confirm-booking';
import { toast } from 'react-toastify';

import {
  FeeAndTax,
  FlightDetails as FlightDetailsType,
  Retrieve,
  Passenger,
  OrderDetails,
  OrderSummary,
  Address,
  Fee,
  Airport,
  Journey,
  Segment,
  Fare,
  BookedRetrieve,
} from '@tff/types/tff';
import { ConfirmBookingRequest } from '@tff/types/tff/confirm-booking-service';

import { useApigeeCall } from '../hooks/MakeApigeeCall';
import JsonFileUploader from './JsonFileUploader';
import sortAndNormalize from '../util/sortAndNormalizePassengers';
import { useLocation } from 'react-router-dom';
import { RerunInvoluntaryChangeProps, useRerunInvoluntaryChange } from '../hooks/use-rerun-involuntary-change';
import AncillariesCard from './AncillariesCard/AncillariesCard';
import ServicesCard from './servicePackages/ServicesCard';
import AncillariesPage from './AncillariesPage/AncillariesPage';
import { useFetchServices } from '../hooks/use-service-packages';
import NdcRebook from './RebookFlight/NdcRebook';
import NskBackButton from './NskBackButton';
import TFFDialog from '../styled-components/TFFDialog';
import TFFTypography from '../styled-components/TFFTypography';
import HistoryTable from './HistoryTable';
import NoticeDialog from './NoticeDialog';
import FlexOptionCard from './FlexOption';
import { calculateTotalPrice } from '../util/priceCalculations';
import { fetchServiceFeeFromCampaign } from '../util/fetchServiceFeeFromCampaign';
import { useBookingContext } from '../hooks/use-booking-context';
import { useAmendFailures } from '../hooks/use-amend-failures';
import { useInvoluntaryChangesAtcom } from '../hooks/use-involuntary-changes-atcom';
import TFFAlert from '../styled-components/TFFAlert';
import { getFareFamily, shouldDisableChangeActions, calculateServicePackageFee } from '../util/flight-details-helpers';

const StyledTableCell = withStyles(() =>
  createStyles({
    root: {
      height: 15,
      padding: 5,
    },
    head: {
      backgroundColor: '#70CBF4',
      color: '#092A5E',
    },
    body: {
      fontSize: 14,
    },
  }),
)(TableCell);

const StyledTableRow = withStyles((theme: Theme) =>
  createStyles({
    root: {
      '&:nth-of-type(odd)': {
        backgroundColor: theme.palette.action.hover,
      },
    },
  }),
)(TableRow);

interface IProps {
  sourceConfig?: ISourceConfig;
  flightDetails: FlightDetailsType;
  midocoFetchedOrder?: FlightDetailsType;
  midocoOrderNo?: number;
  retrieveItem?: Retrieve;
}

type PaxWithPhoneAndEmail = Passenger & { PhoneNumber?: string; Email?: string };

const FlightDetails: React.FC<IProps> = ({
  flightDetails: givenFlightDetails,
  sourceConfig,
  midocoFetchedOrder,
  midocoOrderNo,
  retrieveItem,
}) => {
  const intl = useIntl();
  const { costFormatter, currency, recordLocator } = useBookingContext();

  const location = useLocation();

  const [flightDetails, setFlightDetails] = useState<FlightDetailsType>(givenFlightDetails);

  const orderDetails = flightDetails?.OrderDetails as OrderDetails;
  const orderSummary = flightDetails?.OrderSummary as OrderSummary;

  const outboundJourney = flightDetails?.OrderDetails?.Journeys[0];
  const inboundJourney =
    flightDetails?.OrderDetails?.Journeys.length > 1 ? flightDetails?.OrderDetails?.Journeys[1] : undefined;

  const fareFamily = getFareFamily(outboundJourney, inboundJourney, orderDetails.Journeys.length);

  const changeActionsDisabled = shouldDisableChangeActions(retrieveItem?.product);

  // transform passengers to match required types
  const originPassengers = orderDetails?.Passengers && sortAndNormalize(orderDetails.Passengers);

  const [passengers, setPassengers] = useState<PaxWithPhoneAndEmail[]>([...originPassengers]);

  const [addresses, setAddresses] = useState<Address[]>();
  const { campaigns } = useTypedSelector(({ campaignData }) => campaignData);

  const [activeFlightMenuButton, setActiveFlightMenuButton] = React.useState<string>('');

  const [displayCancelFlight, setDisplayCancelFlight] = useState<boolean>(false);

  const [openNoticeDialog, setOpenNoticeDialog] = useState<boolean>(false);
  const [displayRebookFlight, setDisplayRebookFlight] = useState<boolean>(false);
  const [displayAddAncillaries, setDisplayAddAncillaries] = useState<boolean>(false);
  const [openHistoryDialog, setOpenHistoryDialog] = useState<boolean>(false);
  const [openRebookDialog, setOpenRebookDialog] = useState<boolean>(false);
  const [displayFlightChanges, setDisplayFlightChanges] = useState<boolean>(false);
  const [displayEditPassengers, setDisplayEditPassengers] = useState<boolean>(false);
  const [displayACRTriggerDialog, setDisplayACRTriggerDialog] = useState<boolean>(false);
  const [serviceFeeForCancel, setServiceFeeForCancel] = useState<Fee | undefined>();
  const [airports, setAirports] = useState<Airport[]>();
  const [openHint, setOpenHint] = useState<boolean>(false);
  const [disableButtons, setDisableButtons] = useState<boolean>(changeActionsDisabled);
  const [hasFlightChanges, setHasFlightChanges] = useState<boolean>(false);
  const [displayPricingDetails, setDisplayPricingDetails] = useState<boolean>(false);
  const { sendMessage } = useMessages();
  const { confirmError, confirmStatus, confirmResponse, setConfirmBookingRequest } = useConfirmBooking();

  const [errorMsg, setErrorMsg] = useState<string | null>(null);
  const [successMsg, setSuccessMsg] = useState<string | null>(null);
  const [uploadedJsonFlight, setUploadedJsonFlight] = useState<FlightDetailsType>();
  const [openCancelFlightDialog, setOpenCancelFlightDialog] = useState<boolean>(false);
  const [selectedJourneysToCancel, setSelectedJourneysToCancel] = useState<Journey[]>([]);
  const [rerunInvRequest, setRerunInvRequest] = useState<RerunInvoluntaryChangeProps | undefined>();
  const [selectedAmendBookings, setSelectedAmendBookings] = useState<SelectedAmendBookings>({
    combiHashCodes: [],
    journeyONDs: [],
    offerPrices: [],
  });
  const [servicePackageFee, setServicePackageFee] = useState<number>(0);

  // INFORMATION: Added eslint disable for next hook because giving warnings while developing.
  // Please remove it when you want to develop this feature.
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const { rerunInvoluntaryChangeError, rerunInvoluntaryChangeStatus, rerunInvoluntaryChangeFetchedResult } =
    useRerunInvoluntaryChange(rerunInvRequest!);

  useEffect(() => {
    if (rerunInvoluntaryChangeStatus === 'FETCHED') {
      sendMessage('LOG_ENTRY', 'OCN', 'Successful');
    } else if (rerunInvoluntaryChangeStatus === 'FAILURE') {
      sendMessage('LOG_ENTRY', 'OCN', 'Unsuccessful');
    }
  }, [rerunInvoluntaryChangeStatus]);

  const { setServiceRequest, serviceStatus, serviceResponse } = useFetchServices();
  const { amendFailuresResponse, setAmendFailuresRequest } = useAmendFailures();
  const { involuntaryChangesResponse, setInvoluntaryChangesRequest } = useInvoluntaryChangesAtcom();
  const [openInProgressAlert, setOpenInProgressAlert] = useState<boolean>(false);

  useEffect(() => {
    setServiceRequest(retrieveItem && 'spBookingId' in retrieveItem ? retrieveItem.spBookingId : retrieveItem?.tfmPnr);
  }, [retrieveItem]);

  useEffect(() => {
    if (recordLocator) {
      setAmendFailuresRequest({ archive: false, product: '', recordLocator });
    }
  }, [recordLocator]);

  useEffect(() => {
    if (amendFailuresResponse) {
      if (amendFailuresResponse.some(item => item.status === 'IN_PROGRESS')) {
        setOpenInProgressAlert(true);
      } else {
        setInvoluntaryChangesRequest({ recordLocator });
      }
    }
  }, [amendFailuresResponse]);

  useEffect(() => {
    if (involuntaryChangesResponse && involuntaryChangesResponse.some(item => item.status === 'IN_PROGRESS')) {
      setOpenInProgressAlert(true);
    }
  }, [involuntaryChangesResponse]);

  useEffect(() => {
    setServicePackageFee(
      calculateServicePackageFee(serviceResponse, Object.values(flightDetails.OrderDetails.Passengers).length),
    );
  }, [serviceStatus]);

  useApigeeCall({
    endpoint: `/external-order-notification/${retrieveItem?.product}/${flightDetails?.OrderSummary?.DesignatedAirlineCode}/${recordLocator}/${retrieveItem?.tfmPnr}`,
    call: displayACRTriggerDialog,
    onSuccess: () => {
      setDisplayACRTriggerDialog(false);
      setSuccessMsg('PNR changed triggered, successfully at ACR.');
      sendMessage('LOG_ENTRY', 'AQ_UPDATE', 'Successful');
    },
    onError: er => {
      setErrorMsg(String(er));
      setDisplayACRTriggerDialog(false);
      sendMessage('LOG_ENTRY', 'AQ_UPDATE', 'Unsuccessful');
    },
  });

  const useStyle = makeStyles((theme: Theme) =>
    createStyles({
      root: {
        [theme.breakpoints.down('md')]: {
          width: '95%',
        },
        [theme.breakpoints.up('md')]: {
          width: '85%',
        },
        [theme.breakpoints.up('lg')]: {
          width: '60%',
        },
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        position: 'relative',
        margin: '0 auto',
      },
      backIconWrapper: {
        height: '36px',
        width: '36px',
        borderRadius: '18px',
        border: '2px solid #092A5E',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
      },
      flexRow: {
        display: 'flex',
        flexDirection: 'row',
        alignItems: 'center',
        margin: '20px 0 20px 0',
        alignSelf: 'self-start',
      },
      table: {
        minWidth: 650,
      },
      paymentTableRoot: {
        marginBottom: '20px',
      },
      chargesSummaryHeader: {
        display: 'flex',
        flexDirection: 'row',
        justifyContent: 'space-between',
        width: '100%',
      },
      menuRoot: {
        top: 70,
        transition: 'top 0.5s ease-in',
      },
      menuRootShrunk: {
        top: 0,
        transition: 'top 1s ease-out',
      },
      formControlLabel: {
        padding: 10,
        border: '1px solid grey',
        borderBottom: 'none',
      },
    }),
  );

  const classes = useStyle();

  const { error, status, fetchedAirports } = useRetrieveAirports({
    OndList: orderSummary?.Airports,
  });
  const [isShrunk, setShrunk] = useState(false);

  const checkUnconfirmedSegments = () => {
    flightDetails?.OrderDetails.Journeys.forEach(j => {
      if (
        j.AdditionalParams &&
        Object.keys(j.AdditionalParams).find(ap => ap.startsWith(`TFF_ALERT:UNCONFIRMED_STATE`))
      ) {
        setOpenHint(true);
      }
    });
  };

  const totalTuiFeeAmount: number = useMemo(() => {
    if (!midocoFetchedOrder) {
      return 0;
    }

    let totalFeeAmount: number = 0;
    Object.values(midocoFetchedOrder?.OrderDetails?.Fares ?? {}).forEach((f: Fare) => {
      const serviceFee: FeeAndTax | undefined = f.FeesAndTaxes?.find((ft: FeeAndTax) =>
        ['Serviceentgelt Ticket', 'ServiceFee', 'telefonische Buchung'].includes(ft.Code),
      );
      if (serviceFee) {
        totalFeeAmount += serviceFee.Amount ?? 0;
      }
    });
    return totalFeeAmount;
  }, [midocoFetchedOrder]);

  useEffect(() => {
    const onScroll = () => {
      setShrunk(isShrunk => {
        if (!isShrunk && (document.body.scrollTop > 120 || document.documentElement.scrollTop > 120)) {
          return true;
        }

        if (isShrunk && document.body.scrollTop < 2 && document.documentElement.scrollTop < 2) {
          return false;
        }

        return isShrunk;
      });
    };

    window.addEventListener('scroll', onScroll);
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  useEffect(() => {
    if (!error && status === 'FETCHED' && fetchedAirports) {
      setAirports(fetchedAirports);
    }
  }, [fetchedAirports, error, status]);

  useEffect(() => {
    if (!orderDetails) {
      return;
    }
    checkUnconfirmedSegments();

    if (!displayFlightChanges && location.hash.indexOf('#') > -1) {
      switch (location.hash) {
        case '#changes':
          handleMenuClicked('changes');
          break;
        case '#rebook':
          handleMenuClicked('rebook');
          break;
        case '#cancel':
          handleMenuClicked('cancelBooking');
          break;
        case '#passengerChanges':
          handleMenuClicked('passengerChanges');
          break;
        case '#pricingDetails':
          handleMenuClicked('pricingDetails');
          break;
        default:
          break;
      }
    } else if (!displayCancelFlight && location.hash.indexOf('#cancelBooking') > -1) {
      handleMenuClicked('cancelBooking');
    }

    if (sourceConfig && orderDetails?.Passengers) {
      orderDetails.Addresses && setAddresses(orderDetails.Addresses);

      if (orderSummary?.BookingStatus && orderSummary?.BookingStatus !== 'Closed') {
        const filteredFee = fetchServiceFeeFromCampaign(
          'CANCEL',
          orderDetails,
          orderSummary,
          campaigns,
          sourceConfig.BookingSource,
        );
        setServiceFeeForCancel(filteredFee);
      }
    }

    setHasFlightChanges(false);

    if (midocoFetchedOrder?.OrderDetails?.Journeys?.length && orderDetails?.Journeys?.length) {
      const journeyList: Journey[] = [];
      for (let journey of midocoFetchedOrder.OrderDetails.Journeys) {
        const segmentList: Segment[] = [];
        for (let segment of journey.Segments) {
          if (segment.State === 'OK') {
            segment.State = 'HK';
            segmentList.push(segment);
          } else {
            segmentList.push(segment);
          }
        }
        if (segmentList.length) {
          journey.Segments = segmentList;
          journeyList.push(journey);
        }
      }
      midocoFetchedOrder.OrderDetails.Journeys = journeyList;

      for (let i = 0; i < midocoFetchedOrder.OrderDetails.Journeys.length; i++) {
        const diffedJourney = CheckDifferences(
          midocoFetchedOrder?.OrderDetails?.Journeys[i],
          orderDetails?.Journeys[i],
        );
        if (diffedJourney?.diff?.Segments?.flatMap(d => Object.keys(d)).length) {
          setHasFlightChanges(true);
        }
      }
    }
  }, [orderDetails, sourceConfig]);

  const isNdcFlightOnlyBookingResult = isNdcFlightOnlyBooking(retrieveItem);
  const isTTSBookingResult = isTTSBooking(retrieveItem);
  const isNdcBookingResult = isNdcBooking(retrieveItem);
  const isNskBookingResult = isNskBooking(retrieveItem);

  const totalBookingPrice = calculateTotalPrice(
    orderSummary,
    totalTuiFeeAmount,
    retrieveItem as BookedRetrieve,
    isTTSBookingResult,
    isNdcFlightOnlyBookingResult,
    midocoFetchedOrder?.OrderDetails,
  );

  const renderRebookFlightComponent = () => {
    if (isNdcBookingResult) {
      const isRoundTrip = orderDetails.Journeys?.length > 1;
      return (
        <RebookProvider>
          {orderDetails && orderDetails.Passengers && orderDetails.Journeys && (
            <NdcRebook
              orderDetails={orderDetails}
              orderSummary={orderSummary}
              isRoundTrip={isRoundTrip}
              airports={airports ?? []}
              recordLocator={recordLocator}
              selectedAmendBookings={selectedAmendBookings}
              setSelectedAmendBookings={setSelectedAmendBookings}
              tfmPnr={retrieveItem?.tfmPnr}
              rebookCloser={setDisplayRebookFlight}
              flightDetails={flightDetails}
              midocoOrderNo={midocoOrderNo}
              cancelHandler={() => setActiveFlightMenuButton('')}
              retrieveDb={retrieveItem}
              bookingSource={sourceConfig.BookingSource}
              campaigns={campaigns}
            />
          )}
        </RebookProvider>
      );
    } else {
      if (isNskBookingResult) {
        return (
          <>
            <RebookProvider>
              <NskBackButton
                setDisplayRebookFlight={setDisplayRebookFlight}
                setActiveFlightMenuButton={setActiveFlightMenuButton}
              />
              <NskRebookMain
                orderDetails={orderDetails}
                airports={airports ?? []}
                bookingSource={sourceConfig!.BookingSource!}
                recordLocator={recordLocator}
                selectedAmendBookings={selectedAmendBookings}
                setSelectedAmendBookings={setSelectedAmendBookings}
                tfmPnr={retrieveItem?.tfmPnr}
                rebookCloser={setDisplayRebookFlight}
                flightDetails={flightDetails}
                midocoOrderNo={midocoOrderNo}
                cancelHandler={() => setActiveFlightMenuButton('')}
                retrieveDb={retrieveItem}
              />
            </RebookProvider>
          </>
        );
      }
    }
  };

  const [displayTable, setDisplayTable] = useState<boolean>(false);
  const renderAddAncillaries = () => {
    return (
      <>
        <Box
          sx={{
            '@media (max-width: 1599px)': {
              width: '100%',
            },
            width: '100%',
          }}
        >
          <Button
            sx={{ alignSelf: 'self-start', paddingBottom: 3, justifyContent: 'start', paddingLeft: 0 }}
            onClick={() => {
              if (!displayTable) {
                setDisplayAddAncillaries(false);
              }
              setDisplayTable(false);
            }}
          >
            <div
              style={{
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center',
                height: '18px',
                width: '18px',
                borderRadius: '9px',
                border: '2px solid #092A5E',
              }}
            >
              <ArrowBackIcon color="primary" sx={{ fontSize: '18px' }} />
            </div>
            <Typography color="primary" variant="h6" style={{ marginLeft: '10px', fontWeight: 400, fontSize: '18px' }}>
              {intl.formatMessage({ id: 'ancillaries.navigation.back' })}
            </Typography>
          </Button>
        </Box>
        <AncillariesPage
          flightDetails={flightDetails}
          midocoOrderNo={midocoOrderNo}
          tfmPnr={retrieveItem?.tfmPnr}
          setAncillariesPage={setDisplayAddAncillaries}
          fareFamily={fareFamily}
          displayTable={displayTable}
          setDisplayTable={setDisplayTable}
          isNskBooking={isNskBookingResult}
        />
      </>
    );
  };

  useEffect(() => {
    if (confirmStatus === 'FAILURE' && confirmError) {
      toast.error('Booking confirmation failed: ' + JSON.stringify(confirmError), {
        autoClose: 10000,
        hideProgressBar: false,
        closeOnClick: true,
        pauseOnHover: false,
        draggable: true,
        progress: undefined,
      });
    }

    if (confirmStatus === 'CONFIRMED' && confirmResponse?.success) {
      toast.success('Booking successfully confirmed.');
      setFlightDetails(confirmResponse.flightDetails);
      handleMenuClicked('');
    }
  }, [confirmStatus, confirmResponse, confirmError]);

  /**
   * Confirm the changes at the source and synchronize
   * them to midoco afterwards.
   */
  const onConfirmBookingHandler = () => {
    if (!orderSummary.SourceDetails || !retrieveItem?.tfmPnr) {
      return;
    }
    const confirmBookingRequest: ConfirmBookingRequest = {
      tfmPnr: retrieveItem.tfmPnr,
    };
    setConfirmBookingRequest(confirmBookingRequest);
  };

  const renderCancelFlightComponent = () => {
    return (
      <div
        style={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'flex-start',
          width: '100%',
        }}
      >
        <Button className={classes.flexRow} onClick={() => setDisplayCancelFlight(false)}>
          <div className={classes.backIconWrapper}>
            <ArrowBackIcon color="primary" />
          </div>
          <Typography color="primary" variant="h6" style={{ marginLeft: '10px' }}>
            BACK
          </Typography>
        </Button>
        <CancelBooking
          serviceFeeForCancel={serviceFeeForCancel}
          flightDetails={flightDetails}
          sourceConfig={sourceConfig!}
          onCancel={() => {
            setDisplayCancelFlight(false);
            setActiveFlightMenuButton('');
          }}
          onConfirm={() => alert('Flight Cancelled')}
          airports={airports as Airport[]}
          serviceFeeTotalAmount={totalTuiFeeAmount}
          journeysToCancel={selectedJourneysToCancel}
          servicePackagesFee={isTTSBookingResult ? 0 : servicePackageFee}
          isTTSBooking={isTTSBookingResult}
        />
      </div>
    );
  };

  const renderPayments = () => {
    return (
      <div className={classes.paymentTableRoot}>
        <TableContainer component={Paper}>
          <Table className={classes.table} aria-label="customized table">
            <TableHead>
              <TableRow>
                <StyledTableCell>Id</StyledTableCell>
                <StyledTableCell>Payment Type</StyledTableCell>
                <StyledTableCell>Details</StyledTableCell>
                <StyledTableCell>Status</StyledTableCell>
                <StyledTableCell>Amount</StyledTableCell>
                <StyledTableCell>Date</StyledTableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {orderDetails?.Payments?.map((payment, index) => (
                <StyledTableRow key={index}>
                  <StyledTableCell component="th" scope="row">
                    {payment.Id}
                  </StyledTableCell>
                  <StyledTableCell component="th" scope="row">
                    {payment.Method}
                  </StyledTableCell>
                  <StyledTableCell component="th" scope="row">
                    {payment.Details}
                  </StyledTableCell>
                  <StyledTableCell component="th" scope="row">
                    {payment.Status}
                  </StyledTableCell>
                  <StyledTableCell component="th" scope="row">
                    {costFormatter.format(payment.Amount)}
                  </StyledTableCell>
                  <StyledTableCell component="th" scope="row">
                    {payment.CreatedDate ? format(new Date(payment.CreatedDate), 'dd.MM.yyyy HH:mm') : ''}
                  </StyledTableCell>
                </StyledTableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      </div>
    );
  };

  const renderPricingDetails = () => {
    return (
      <div
        style={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'flex-start',
          width: '100%',
        }}
      >
        <div className={classes.chargesSummaryHeader}>
          <Button
            className={classes.flexRow}
            style={{ margin: 0, marginTop: '20px' }}
            onClick={() => {
              setDisplayEditPassengers(false);
              setOpenNoticeDialog(false);
              setOpenHistoryDialog(false);
              setOpenRebookDialog(false);
              setDisplayFlightChanges(false);
              setDisplayRebookFlight(false);
              setDisplayAddAncillaries(false);
              setDisplayCancelFlight(false);
              setDisplayPricingDetails(false);
            }}
          >
            <div className={classes.backIconWrapper}>
              <ArrowBackIcon color="primary" />
            </div>
            <Typography color="primary" variant="h6" style={{ marginLeft: '10px' }}>
              BACK
            </Typography>
          </Button>
        </div>
        <div style={{ marginTop: '20px', width: '100%' }}>
          {orderDetails?.Payments && renderPayments()}
          <ChargesSummary
            orderDetails={orderDetails}
            sourceConfig={sourceConfig}
            retrieveDb={retrieveItem as BookedRetrieve}
            isTTSBooking={isTTSBookingResult}
          />
        </div>
        <Typography variant="body1">{`${intl.formatMessage({
          id: 'chargesSummary.allPrices',
        })} ${currency}`}</Typography>
      </div>
    );
  };

  const handleJsonFileUpload = (json: any, type?: string) => {
    const handlers = {
      flightdetails: () => setUploadedJsonFlight(json as FlightDetailsType),
      orderdetails: () => {
        const flight = { ...flightDetails, OrderDetails: json };
        setUploadedJsonFlight(flight as FlightDetailsType);
      },
      journeys: () => {
        const flight = {
          ...flightDetails,
          OrderDetails: {
            ...flightDetails.OrderDetails,
            Journeys: json,
          },
        };
        setUploadedJsonFlight(flight as FlightDetailsType);
      },
    };
    type && handlers[type.toLowerCase()]?.();
  };

  const renderFlightChangesComponent = () => {
    const journeysLength = orderDetails.Journeys.length;
    return (
      <div
        style={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'flex-start',
          width: '100%',
        }}
      >
        <Button className={classes.flexRow} onClick={() => setDisplayFlightChanges(false)}>
          <div className={classes.backIconWrapper}>
            <ArrowBackIcon color="primary" />
          </div>
          <Typography color="primary" variant="h6" style={{ marginLeft: '10px' }}>
            {journeysLength
              ? intl.formatMessage({ id: 'flightChanges.yourFlightsHaveChanges' })
              : intl.formatMessage({ id: 'flightChanges.yourFlightHasChanges' })}
          </Typography>
        </Button>

        {window.ENV?.DEBUG_MODE && (
          <JsonFileUploader
            onJsonUpload={handleJsonFileUpload}
            initialJourneys={flightDetails?.OrderDetails?.Journeys}
          />
        )}
        <FlightChanges
          journeysFromMidoco={midocoFetchedOrder?.OrderDetails.Journeys!}
          journeysFromSource={uploadedJsonFlight ? uploadedJsonFlight.OrderDetails.Journeys : orderDetails.Journeys}
          airports={airports as Airport[]}
          confirmStatus={confirmStatus}
          confirmError={confirmError}
          onConfirmBookingAfterAdustmentFN={onConfirmBookingHandler}
          onConfirmChangeFN={onConfirmBookingHandler}
          onCancelBookingFN={() => {
            handleMenuClicked('cancelBooking');
            setActiveFlightMenuButton('');
          }}
        />
      </div>
    );
  };

  const renderMsgDialog = () => {
    return (
      <CustomDialog width="md" initialOpen={errorMsg !== null || successMsg !== null} form transparent>
        <Collapse in={errorMsg !== null || successMsg !== null}>
          {(errorMsg !== null || successMsg !== null) && (
            <Alert
              severity={errorMsg !== null ? 'error' : 'success'}
              action={
                <IconButton
                  aria-label="close"
                  color="inherit"
                  size="small"
                  onClick={() => {
                    setErrorMsg(null);
                    setSuccessMsg(null);
                  }}
                >
                  <CloseIcon fontSize="inherit" />
                </IconButton>
              }
            >
              <AlertTitle>
                <strong>
                  {errorMsg !== null
                    ? intl.formatMessage({ id: 'global.error' })
                    : intl.formatMessage({ id: 'global.success' })}
                </strong>
              </AlertTitle>
              <Typography variant="h6" color="primary">
                {errorMsg}
                {successMsg}
              </Typography>
            </Alert>
          )}
        </Collapse>
      </CustomDialog>
    );
  };

  const handlePaxBack = () => {
    setDisplayEditPassengers(false);
    setPassengers(originPassengers);
  };

  const updatePassengers = responseData => {
    try {
      //setPassengers(responseData.passengers);
      //NDC returns an object, nsk an array?
      flightDetails.OrderDetails.Passengers =
        responseData.passengers ?? Object.values(responseData.airlineDetails?.OrderDetails?.Passengers);
      setSuccessMsg(intl.formatMessage({ id: 'edit.passenger.success' }));
      sendMessage('LOG_ENTRY', 'EDIT_PASSENGERS', 'Successful');
    } catch (er) {
      console.error(er);
      setErrorMsg(intl.formatMessage({ id: 'edit.passenger.unexpectedResponse' }));
      sendMessage('LOG_ENTRY', 'EDIT_PASSENGERS', 'Unsuccessful');
    }
    setDisplayEditPassengers(false);
  };

  const handlePassengerEditError = (error: any) => {
    sendMessage('LOG_ENTRY', 'EDIT_PASSENGERS', 'Unsuccessful');
  };
  const renderEditPassengersComponent = () => {
    const backText = intl.formatMessage({ id: 'edit.passenger.back' }); //`Passengers Details`;
    if (!passengers || !sourceConfig) {
      return <></>;
    }
    return (
      <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', width: '100%' }}>
        <Button className={classes.flexRow} onClick={() => setDisplayEditPassengers(false)}>
          <div className={classes.backIconWrapper}>
            <ArrowBackIcon color="primary" />
          </div>
          <Typography color="primary" variant="h6" style={{ marginLeft: '10px' }}>
            {backText.toUpperCase()}
          </Typography>
        </Button>
        <Card style={{ backgroundColor: '#E2F3FE', width: '100%' }}>
          <CardContent>
            <Typography style={{ paddingBottom: '10px' }} variant="h5" color="primary">
              <strong>{intl.formatMessage({ id: 'edit.passenger.editPassengers' })}</strong>
            </Typography>
            <PassengersEditForm
              flightDetails={flightDetails}
              sourceGroup={sourceConfig.SourceGroup}
              midocoOrderNo={midocoOrderNo}
              onSuccess={updatePassengers}
              onError={handlePassengerEditError}
              onBack={handlePaxBack}
            />
          </CardContent>
        </Card>
      </div>
    );
  };

  const handleMenuClicked = (buttonId: string) => {
    setActiveFlightMenuButton(buttonId);

    setOpenCancelFlightDialog(false);
    setDisplayCancelFlight(false);
    setOpenNoticeDialog(false);
    setDisplayRebookFlight(false);
    setDisplayAddAncillaries(false);
    setDisplayEditPassengers(false);
    setOpenHistoryDialog(false);
    setOpenRebookDialog(false);
    setDisplayFlightChanges(false);
    setDisplayPricingDetails(false);
    setDisplayACRTriggerDialog(false);

    switch (buttonId) {
      case `pricingDetails`:
        setDisplayPricingDetails(true);
        break;
      case `notice`:
        setOpenNoticeDialog(true);
        break;
      case `rebook`:
        if (orderDetails.Journeys[0].FareFamily === 'Pure') {
          setOpenRebookDialog(true);
        } else {
          setDisplayRebookFlight(true);
        }

        break;
      case `history`:
        setOpenHistoryDialog(true);
        break;
      case `cancelBooking`:
        if (
          sourceConfig!.BookingSource === 'TUI-NSK' &&
          orderSummary.JourneyType !== 'ONEWAY' &&
          window.ENV?.ENABLE_SPLIT_CANCEL === true
        ) {
          setOpenCancelFlightDialog(true);
          setSelectedJourneysToCancel([]);
        } else {
          setDisplayCancelFlight(true);
          setSelectedJourneysToCancel(orderDetails.Journeys);
        }
        break;

      case `changes`:
        setDisplayFlightChanges(true);
        break;
      case `passengerChanges`:
        setDisplayEditPassengers(true);
        break;
      case 'triggerPNRchangeToAcr':
        setDisplayACRTriggerDialog(true);
        break;
      case 'ocnman':
        if (recordLocator && sourceConfig?.BookingSource) {
          setRerunInvRequest({
            recordLocator: recordLocator,
            bookingSource: sourceConfig.BookingSource! as NdcBookingSource,
          });
        }
        break;
      default:
        break;
    }
  };



  const renderHintDialog = () => {
    return (
      <CustomDialog width="md" initialOpen={openHint} form transparent>
        <Collapse in={openHint}>
          <Alert
            severity="error"
            action={
              <IconButton
                aria-label="close"
                color="inherit"
                size="small"
                onClick={() => {
                  setOpenHint(false);
                  setDisableButtons(true);
                }}
              >
                <CloseIcon fontSize="inherit" />
              </IconButton>
            }
          >
            <AlertTitle>
              <strong>{intl.formatMessage({ id: 'hint.info' })}</strong>
            </AlertTitle>
            <Typography variant="h6" color="primary">
              {intl.formatMessage({ id: 'hint.involuntaryChange.message' })}
            </Typography>
          </Alert>
        </Collapse>
      </CustomDialog>
    );
  };

  const renderRebookDialog = () => {
    return (
      <>
        <CustomDialog
          onCancel={() => {
            setOpenRebookDialog(false);
            setActiveFlightMenuButton('');
          }}
          initialOpen={openRebookDialog}
          confirmButton
          confirmButtonText={intl.formatMessage({ id: 'pages.flightDetails.rebook.model.confirmation' })}
          confirmButtonStyle={{
            backgroundColor: '#F2E30F',
            color: 'black',
          }}
          onConfirm={() => {
            setOpenRebookDialog(false);
            setDisplayRebookFlight(true);
          }}
          width="md"
          cancelButtonText={intl.formatMessage({ id: 'global.no' })}
          titleBg="#E2F3FE"
          title={
            <Typography variant="h5" align="left" fontWeight="bold">
              {intl.formatMessage({ id: 'pages.flightDetails.rebook.model.headline' })}
            </Typography>
          }
        >
          <Typography variant="h6" align="left" style={{ marginTop: '20px' }}>
            {intl.formatMessage({ id: 'pages.flightDetails.rebook.model.processing' })}
          </Typography>
        </CustomDialog>
      </>
    );
  };

  const renderCancelJourneyDetails = (j: Journey) => {
    const origin = j.Segments[0];
    const departureDate = new Date(origin.DepartureTime).toLocaleDateString();
    const departureTime = new Date(origin.DepartureTime).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
    const originAirport = fetchedAirports?.find(a => a.id === origin.Origin);

    const destination = j.Segments[j.Segments.length === 0 ? 0 : j.Segments.length - 1];
    const arrivalTime = new Date(destination.ArrivalTime).toLocaleTimeString([], {
      hour: '2-digit',
      minute: '2-digit',
    });
    const destinationAirport = fetchedAirports?.find(a => a.id === destination.Destination);

    return (
      <p>
        {departureDate} <br />
        <span>
          {departureTime} {originAirport?.nameDE}
        </span>
        <span> - </span>
        <span>
          {arrivalTime} {destinationAirport?.nameDE}
        </span>
      </p>
    );
  };

  const renderCancelBookingDialog = () => {
    const journeys = flightDetails.OrderDetails.Journeys;
    return (
      <TFFDialog
        dialogId="cancelBookingDialog"
        isOpen={openCancelFlightDialog}
        confirmButton
        confirmButtonText={intl.formatMessage({ id: 'pages.flightCombination.continue' })}
        onConfirm={() => {
          setOpenCancelFlightDialog(false);
          setDisplayCancelFlight(true);
        }}
        confirmDisabled={selectedJourneysToCancel?.length === 0}
        cancelButton
        cancelButtonText={intl.formatMessage({ id: 'pages.flightCombination.cancel' })}
        onCancel={() => {
          setOpenCancelFlightDialog(false);
          setSelectedJourneysToCancel([]);
          setActiveFlightMenuButton('');
        }}
        header={
          <TFFTypography
            text={intl.formatMessage({ id: 'pages.flightCombination.cancelBookingTitle' }).toUpperCase()}
            variant="lead"
            size="large"
            fontWeight="bold"
          />
        }
        extraStyle={{
          padding: '10px',
        }}
      >
        <TFFTypography
          text={intl.formatMessage({ id: 'pages.flightCombination.whichFlightToCancel' })}
          variant="base"
          size="medium"
        />
        <FormControl style={{ width: '100%', marginTop: '20px' }}>
          <RadioGroup
            aria-labelledby="radio-buttons-group-label"
            name="radio-buttons-group"
            style={{ marginLeft: '20px' }}
          >
            {journeys.map((j, index) => {
              return (
                <FormControlLabel
                  key={j.Id}
                  className={classes.formControlLabel}
                  value={j.Ond}
                  control={<Radio></Radio>}
                  label={
                    <>
                      <Typography variant="h6" align="left" color="primary" fontWeight="bold">
                        {intl.formatMessage({
                          id:
                            index === 0
                              ? 'pages.flightCombination.cancelOutbound'
                              : 'pages.flightCombination.cancelReturn',
                        })}
                      </Typography>
                      {renderCancelJourneyDetails(j)}
                    </>
                  }
                  onClick={() => handleCancelBookingSelection(j)}
                />
              );
            })}
            <FormControlLabel
              className={classes.formControlLabel}
              style={{ borderBottom: '1px solid grey' }}
              value="all"
              control={<Radio></Radio>}
              label={
                <Typography variant="h6" align="left" color="primary" fontWeight="bold">
                  {intl.formatMessage({ id: 'pages.flightCombination.cancelAll' })}
                </Typography>
              }
              onClick={() => handleCancelBookingSelection()}
            />
          </RadioGroup>
        </FormControl>
      </TFFDialog>
    );
  };

  const handleCancelBookingSelection = (journey?: Journey) => {
    if (journey) {
      setSelectedJourneysToCancel([journey]);
    } else {
      setSelectedJourneysToCancel(orderDetails.Journeys);
    }
  };

  const showMain = () => {
    return (
      !displayRebookFlight &&
      !displayAddAncillaries &&
      !displayCancelFlight &&
      !displayFlightChanges &&
      !displayEditPassengers &&
      !displayPricingDetails
    );
  };

  const renderFlightDetails = () => {
    const displayMain = showMain();
    return (
      <div className={classes.root}>
        <div
          style={{ position: 'fixed', width: '100vw', zIndex: 2 }}
          className={`${isShrunk ? classes.menuRootShrunk : classes.menuRoot}`}
        >
          <BookingDetailHeader
            dbRetrieveItem={retrieveItem}
            flightDetails={flightDetails}
            sourceConfig={sourceConfig}
            midocoFlightDetails={midocoFetchedOrder}
            midocoOrderNo={midocoOrderNo}
            onMenuClicked={handleMenuClicked}
            hasFlightChanges={hasFlightChanges}
            disableButtons={disableButtons}
            isShrunk={isShrunk}
            activeButton={activeFlightMenuButton}
            totalBookingPrice={totalBookingPrice}
          />
        </div>
        {/**divider */}
        <div style={{ height: '260px', width: '100%' }}></div>
        {openInProgressAlert && (
          <TFFAlert
            variant="info"
            text={intl.formatMessage({ id: 'pages.flightDetails.inProgress' })}
            closeButton
            handleClose={() => setOpenInProgressAlert(false)}
            extraStyles={{
              position: 'absolute',
              right: '-20px',
              top: '260px',
              zIndex: '1', //to make sure it is in front
            }}
          />
        )}
        {displayRebookFlight && renderRebookFlightComponent()}
        {displayAddAncillaries && renderAddAncillaries()}
        {displayCancelFlight && renderCancelFlightComponent()}
        {displayFlightChanges && renderFlightChangesComponent()}
        {displayEditPassengers && renderEditPassengersComponent()}
        {displayPricingDetails && renderPricingDetails()}
        {displayMain && (
          <div
            style={{
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              width: '100%',
            }}
          >
            {orderDetails &&
              orderDetails.Passengers &&
              recordLocator &&
              orderDetails.Journeys?.map((j: Journey, index) => {
                return (
                  <FlightDetailCard
                    orderDetails={orderDetails}
                    key={j.JourneyIdentifier}
                    journey={j}
                    isOutbound={index === 0}
                    airports={airports ?? []}
                    disableButtons={disableButtons}
                    bookingSource={sourceConfig!.BookingSource!}
                    recordLocator={recordLocator}
                    tfmPnr={retrieveItem?.tfmPnr ?? 'NOT SET'}
                    midocoOrderNo={midocoOrderNo}
                  />
                );
              })}
            {
              <AncillariesCard
                flightDetails={flightDetails}
                handleEdit={() => setDisplayAddAncillaries(true)}
                recordLocator={recordLocator}
                tfmPnr={retrieveItem?.tfmPnr}
                midocoOrderNo={midocoOrderNo}
                bookingSource={sourceConfig?.BookingSource}
              />
            }
            {passengers && (
              <PassengerDetailCard
                allowEdit={!changeActionsDisabled}
                handleEditBtn={() => setDisplayEditPassengers(!displayEditPassengers)}
                flightDetails={flightDetails}
                midocoOrderNo={midocoOrderNo}
              />
            )}
            {addresses && midocoFetchedOrder && sourceConfig && (
              <ContactsDetailCard
                flightDetails={flightDetails}
                midocoOrderNo={midocoOrderNo}
                allowEdit={!changeActionsDisabled}
                midocoDetails={midocoFetchedOrder}
                addresses={orderDetails?.Addresses ?? []}
                sourceGroup={sourceConfig.SourceGroup}
                handleEditBtn={() => setDisplayEditPassengers(!displayEditPassengers)}
              />
            )}
            {!isTTSBookingResult && (
              <ServicesCard
                serviceResponse={serviceResponse}
                passengers={Object.values(flightDetails.OrderDetails.Passengers)}
              />
            )}
            {window.ENV?.ENABLE_FLEX_OPTION && !isTTSBookingResult && <FlexOptionCard flexResponse={serviceResponse} />}
            <PricingDetailsCard
              flightDetails={flightDetails}
              midocoFlightDetails={midocoFetchedOrder}
              serviceFeeTotalAmount={totalTuiFeeAmount}
              onDetailClick={() => setDisplayPricingDetails(true)}
              servicePackageFee={servicePackageFee}
              retrieveDb={retrieveItem as BookedRetrieve}
              isNdcFlightOnlyBooking={isNdcFlightOnlyBookingResult}
              isTTSBooking={isTTSBookingResult}
              totalBookingPrice={totalBookingPrice}
            />
          </div>
        )}
        {window.ENV?.DEBUG_MODE && (
          <>
            <ReactJson name={'DEBUG:FlightDetails'} src={flightDetails} collapsed={true} />
            <ReactJson name={'DEBUG:MidocoDetails'} src={midocoFetchedOrder!} collapsed={true} />
            <ReactJson name={'DEBUG:fetchedRetrieveDbItem'} src={retrieveItem!} collapsed={true} />
          </>
        )}
        {openNoticeDialog && (
          <NoticeDialog
            isNdcSystem={orderSummary.SourceDetails?.System === 'TUI-NDC'}
            onClose={() => {
              setOpenNoticeDialog(false);
              setActiveFlightMenuButton('');
            }}
          />
        )}
        {openHistoryDialog && (
          <CustomDialog
            onCancel={() => {
              setOpenHistoryDialog(false);
              setActiveFlightMenuButton('');
            }}
            confirmButton={false}
            initialOpen={openHistoryDialog}
            width="lg"
            confirmButtonText={intl.formatMessage({ id: 'pages.flightCombination.addNote' })}
            cancelButtonText={intl.formatMessage({ id: 'pages.flightCombination.close' })}
            title={
              <Typography variant="h5" align="left">
                {intl.formatMessage({ id: 'pages.flightCombination.detailsRecordlocator' })} : {recordLocator}
              </Typography>
            }
          >
            <HistoryTable />
          </CustomDialog>
        )}
        {renderHintDialog()}
        {renderMsgDialog()}
        {renderRebookDialog()}
        {renderCancelBookingDialog()}
      </div>
    );
  };

  return renderFlightDetails();
};

export default FlightDetails;
